import React, { useCallback, useContext } from 'react';

import type {
  AgentName,
  AiModel,
  AnalysisQuestionRun,
  SwapAgentToolCall,
  ToolCallType,
  WorkflowAiResult,
  WorkflowAiToolCallResult,
} from '@aline/common';
import {
  assertNeverSafely,
  getExternalConnectDescription,
  getExternalConnectTitle,
  getExternalToolCallTitle,
  getExternalToolkitLabel,
  getExternalToolResolutionLabel,
  getIsAgentMessageToolCallProcessing,
  getPendingToolApprovalForToolCall,
  getToolApprovalForToolCall,
  getToolOutputForToolCall,
  getWorkflowRoute,
  toolCallSchema,
  WorkflowAiResultSchema,
  WorkflowAiToolCallResultSchema,
} from '@aline/common';
import {
  getSkillAuthoringPresentation,
  getSkillAuthoringToolOutput,
} from '@aline/common/agent-skills/skill-authoring-presentation';
import { getReportCreationPresentation } from '@aline/common/report-creation-presentation';
import { link } from '@aline/routes';
import { CircularProgress, Link as MuiLink, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  ArchiveRestore,
  BookOpenText,
  ChartColumnBig,
  CheckCircle,
  MessageCircle,
  Search,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { AskUserInlineSummary, getAskUserAnswers } from 'src/components/ai/chat/AskUser/AskUser';
import { MentionEntityContext } from 'src/components/ai/chat/chat-hooks/useMentionAccessContext';
import type { SkillVersionMentionEntity } from 'src/components/ai/chat/chat-hooks/useMentionAccessContext';
import { AlineButton, AlineTooltip, Spacer } from 'src/components/generic';
import type { SimpleIconInterface } from 'src/components/icons/icon-utils';
import { ToolCallCalculatorIcon } from 'src/components/icons/ToolCallCalculatorIcon';
import { ToolCallGetWebpagesIcon } from 'src/components/icons/ToolCallGetWebpagesIcon';
import { ToolCallModelFallbackIcon } from 'src/components/icons/ToolCallModelFallbackIcon';
import { ToolCallQueryDocumentIcon } from 'src/components/icons/ToolCallQueryDocumentIcon';
import { ToolCallQuestionMarkIcon } from 'src/components/icons/ToolCallQuestionMarkIcon';
import { ToolCallReadDocumentIcon } from 'src/components/icons/ToolCallReadDocumentIcon';
import { ToolCallResearchIcon } from 'src/components/icons/ToolCallResearchIcon';
import { ToolCallSearchIcon } from 'src/components/icons/ToolCallSearchIcon';
import { ToolCallSwapAgentIcon } from 'src/components/icons/ToolCallSwapAgentIcon';
import { WarningIcon } from 'src/components/icons/WarningIcon';
import { Apollo } from 'src/gql/generated';
import { handleOpenInAlineTab } from 'src/helpers';
import { getTypographyForSx, important } from 'src/theme';
import { getIsPlainRecord } from 'src/utils/get-is-plain-record';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

import { AnalysisQueryPanel } from './analysis-query-panel';
import {
  describeFinishedQuestionRun,
  getQuestionRunBatchId,
  getQuestionRunProgress,
  getQuestionRunWaitCopy,
  getQuestionRunWaitForBatch,
  INTERRUPTED_QUESTION_RUN_COPY,
} from './analysis-question-run';
import { getAnalysisPanelSource } from './analysis-result-rows';
import type { AnalysisColumnLabels, AnalysisRow } from './analysis-result-rows';
import { getWorkflowResultCandidates, WorkflowResultCandidateList } from './CodeWorkflowElement';
import { QuestionRunProgressBar } from './question-run-progress-bar';
import type { QuestionRunBarState } from './question-run-progress-bar';
import { SaveQuestionRunAsReportButton } from './save-question-run-as-report-button';
import { getIsTerminalAssistantStatus } from '../../../chat-hooks/harness-visible-work';
import type { ChatAssistantMessage, DocumentExtractionPause } from '../../../chat-types';
import { getIsPinnedSkillCardToolCallId } from '../../../ChatPanel/pinned-skill-card-id';
import { getSkillLoadVersionIds } from '../../../ChatPanel/skill-load-version-ids';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { useChatAssistantMessage } from '../../ChatPanelMarkdown';
import { parseCodeBlockJson } from '../../helpers/parseCodeBlockJson';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';
import { LinkChip } from '../AnchorElement/AnchorElement';

const toolCallIconMap: Record<ToolCallType, React.ComponentType<SimpleIconInterface>> = {
  askUser: ToolCallQuestionMarkIcon,
  assignTask: ToolCallWorkflowIcon,
  calculator: ToolCallCalculatorIcon,
  commentOnTask: ToolCallCommentOnTaskIcon,
  compactPriorOutput: ToolCallCompactPriorOutputIcon,
  completeTask: ToolCallCompleteTaskIcon,
  completeWorkflow: ToolCallCompleteTaskIcon,
  createReport: ToolCallReportIcon,
  createSkill: ToolCallSkillIcon,
  documentBaseAnalysis: ToolCallQueryDocumentIcon,
  // Shares the reaching-outside-Aline icon with web page fetches.
  externalConnect: ToolCallGetWebpagesIcon,
  externalTool: ToolCallGetWebpagesIcon,
  generatePlaybook: ToolCallPlaybookIcon,
  getTask: ToolCallGetTaskIcon,
  getTaskAgentThread: ToolCallGetTaskIcon,
  getWebPageContents: ToolCallGetWebpagesIcon,
  listTasks: ToolCallWorkflowIcon,
  loadSkill: ToolCallSkillIcon,
  managePhases: ToolCallWorkflowIcon,
  modelFallback: ToolCallModelFallbackIcon,
  queryDocument: ToolCallQueryDocumentIcon,
  readDocument: ToolCallReadDocumentIcon,
  readIntakeForm: ToolCallReadDocumentIcon,
  readPlaybook: ToolCallReadDocumentIcon,
  readSkill: ToolCallSkillIcon,
  readSkillResource: ToolCallReadDocumentIcon,
  readWorkflow: ToolCallWorkflowIcon,
  reassignTask: ToolCallWorkflowIcon,
  replyInChannelThread: ToolCallCommentOnTaskIcon,
  reportAnalysis: ToolCallQueryDocumentIcon,
  research: ToolCallResearchIcon,
  runExtractionQuestions: ToolCallQueryDocumentIcon,
  runPlaybook: ToolCallPlaybookIcon,
  search: ToolCallSearchIcon,
  searchPlaybooks: ToolCallSearchIcon,
  searchSkillResources: ToolCallSearchIcon,
  searchSkills: ToolCallSearchIcon,
  selectDocuments: ToolCallReadDocumentIcon,
  semanticSearch: ToolCallSearchIcon,
  swapAgent: ToolCallSwapAgentIcon,
  updateIntakeForm: ToolCallWorkflowIcon,
  updateSkill: ToolCallSkillIcon,
  updateTaskDescription: ToolCallWorkflowIcon,
  workflowCollectIntakeForm: ToolCallWorkflowIcon,
  workflowCollectLaunchInfo: ToolCallWorkflowIcon,
  workflowCreateDraft: ToolCallWorkflowIcon,
  workflowLaunchTemplate: ToolCallWorkflowIcon,
  workflowSearchExisting: ToolCallWorkflowIcon,
  workflowSearchTemplates: ToolCallWorkflowIcon,
};

export const toolIconProps: SimpleIconInterface = {
  fill: '#ffffff',
  height: 14,
  stroke: '#c0c0c0',
  width: 14,
};

function ToolCallIcon({ type }: { type: ToolCallType }) {
  const Icon = toolCallIconMap[type];
  return <Icon {...toolIconProps} />;
}

function ToolCallCompactPriorOutputIcon(props: SimpleIconInterface) {
  return <ArchiveRestore {...props} />;
}

function ToolCallCommentOnTaskIcon(props: SimpleIconInterface) {
  return <MessageCircle {...props} />;
}

function ToolCallCompleteTaskIcon(props: SimpleIconInterface) {
  return <CheckCircle {...props} />;
}

function ToolCallGetTaskIcon(props: SimpleIconInterface) {
  return <Search {...props} />;
}

/** The same icon playbooks carry in the left nav, the tab bar and mentions. */
function ToolCallPlaybookIcon(props: SimpleIconInterface) {
  return <BookOpenText {...props} />;
}

/** The same icon skills carry in the admin list and the `/` picker. */
function ToolCallSkillIcon(props: SimpleIconInterface) {
  return <Sparkles {...props} />;
}

/** The same icon reports carry in the left nav. */
function ToolCallReportIcon(props: SimpleIconInterface) {
  return <ChartColumnBig {...props} />;
}

function ToolCallWorkflowIcon(props: SimpleIconInterface) {
  return <Workflow {...props} />;
}

const shimmer = keyframes`
  0% { background-position: -100% -100%; }
  100% { background-position: 100% 100%; }
`;

export const useToolCallStyles = makeStyles()(theme => ({
  clickableTitleContainer: {
    cursor: 'pointer',
  },
  description: {
    ...getTypographyForSx(theme.typography.body1),
    fontSize: 12,
    fontWeight: 400,
    marginTop: theme.spacing(0.25),
    wordBreak: 'break-word',
  },
  iconDiv: {
    alignItems: 'center',
    display: 'flex',
    flex: 0,
    justifyContent: 'center',
    maxWidth: 16,
    minWidth: 16,
  },
  inlineWorkflowResultSummary: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.secondaryType,
    fontSize: theme.typography.pxToRem(12),
  },
  inlineWorkflowResultWrapper: {
    marginTop: theme.spacing(1),
  },
  processingText: {
    // Apply shimmer to element and all deeply nested descendants
    '&, & *': {
      WebkitBackgroundClip: important('text'),
      animation: important(`${shimmer} 1s infinite linear`),
      background: important('linear-gradient(45deg, #999 25%, #666 50%, #999 75%)'),
      backgroundClip: important('text'),
      backgroundSize: important('200% 200%'),
      color: important('transparent'),
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    margin: theme.spacing(1, 0),
  },
  row: {
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  stopRunButton: {
    flexShrink: 0,
    fontSize: 12,
    gap: theme.spacing(0.75),
    minHeight: 24,
    padding: theme.spacing(0.25, 1),
  },
  stopSquare: {
    backgroundColor: 'currentColor',
    borderRadius: 2,
    flexShrink: 0,
    height: 8,
    width: 8,
  },
  textDiv: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    fontWeight: 500,
  },
  titleContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  verticalLine: {
    backgroundColor: theme.appColors.rule,
    height: '100%',
    left: -0.5,
    position: 'relative',
    width: 1,
  },
}));

export const CodeToolCallElement = React.memo(function ToolCallElement({
  node,
}: MarkdownComponentProps): JSX.Element | null {
  const { classes } = useToolCallStyles();
  const message = useChatAssistantMessage();
  const codeBlockInfo = getCodeBlockInfoFromNode(node);
  const mentionEntityContext = useContext(MentionEntityContext);
  if (!codeBlockInfo) return null;

  const parsed = parseCodeBlockJson(codeBlockInfo.code, toolCallSchema, 'CodeToolCallElement');
  const toolCall = parsed.ok ? parsed.data : null;
  if (
    toolCall?.type === 'loadSkill' &&
    getIsLoadShownByPinnedCard({
      loadedVersionIds: getSkillLoadVersionIds(message.data.uiParts ?? []),
      toolCall,
    })
  )
    return null;
  const toolApproval = toolCall
    ? getToolApprovalForToolCall(message.data.uiParts, toolCall.id)
    : null;
  const toolOutput = toolCall ? getToolOutputForToolCall(message.data.uiParts, toolCall.id) : null;
  const clientInputLookup = getClientInputToolLookup(toolOutput);
  const toolInput = toolOutput?.part.input ?? toolApproval?.part.input;
  const reportPresentation =
    toolCall?.type === 'createReport'
      ? getReportCreationPresentation({
          running: !getIsTerminalAssistantStatus(message.status),
          toolCall,
          uiParts: message.data.uiParts,
        })
      : null;
  /**
   * Client-input and needsApproval tools can leave the agent-message tool status
   * finished (or unset) while still waiting on the user. Treat pending client
   * input and unresolved approvals as processing so titles stay in-progress.
   */
  const isTurnOver = getIsTerminalAssistantStatus(message.status);
  const runCardWait = getRunCardWait({
    isTurnOver,
    pause: message.data.documentExtractionPause,
    toolCall,
  });
  const isProcessing =
    reportPresentation?.processing ??
    Boolean(
      runCardWait ||
      (toolCall && getIsAgentMessageToolCallProcessing(message.data.toolCalls, toolCall.id)) ||
      (toolCall && getIsClientInputToolAwaitingUser(toolCall.type, clientInputLookup)) ||
      (toolCall &&
        getIsAwaitingApproval({
          isTurnOver,
          toolCallId: toolCall.id,
          uiParts: message.data.uiParts,
        }))
    );
  const toolInfo =
    reportPresentation ??
    getToolInfo({
      approvalReason: toolApproval?.reason,
      approved: toolApproval?.approved,
      clientInput: clientInputLookup,
      loading: isProcessing,
      mentionEntityContext,
      toolCall,
      toolInput,
      toolOutput:
        toolCall?.type === 'createSkill' || toolCall?.type === 'updateSkill'
          ? getSkillAuthoringToolOutput(message.data.uiParts, toolCall.id)
          : toolOutput?.output,
    });
  const workflowResult = getWorkflowResult(toolOutput?.output);
  const inlineWorkflowSearchResult = getInlineWorkflowSearchResult({
    toolCall,
  });
  const workflowLaunchDetails =
    toolCall?.type === 'workflowLaunchTemplate'
      ? {
          approved: toolApproval?.approved,
          result: workflowResult,
          templateName: getWorkflowLabel(toolCall.templateWorkflowId, mentionEntityContext),
          templateWorkflowId: toolCall.templateWorkflowId,
          workflowName:
            workflowResult?.status === 'launched'
              ? getWorkflowLabel(workflowResult.workflow.workflowId, mentionEntityContext)
              : toolCall.workflowName,
        }
      : null;
  const loadSkillInfo =
    toolCall?.type === 'loadSkill'
      ? getLoadSkillToolInfo({
          loading: isProcessing,
          skillVersion: mentionEntityContext.skillVersions.get(toolCall.versionId) ?? null,
          toolCall,
        })
      : null;
  const questionRunWaitInfo = getAnalysisQuestionRunWaitInfo({
    loading: isProcessing,
    pause: message.data.documentExtractionPause,
    toolCall,
  });
  const shownInfo = loadSkillInfo ?? questionRunWaitInfo ?? toolInfo;
  const showProcessing = isProcessing || Boolean(loadSkillInfo?.waiting);
  const stoppableBatchId = getStoppableBatchId({
    isAnalysisWaiting: Boolean(questionRunWaitInfo) && isProcessing,
    pause: message.data.documentExtractionPause,
    runCardWait,
    toolCall,
  });
  const questionRunBar = getQuestionRunBarState({
    isProcessing,
    pause: message.data.documentExtractionPause,
    runCardWait,
    toolCall,
  });

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <div className={classes.iconDiv}>
          <ToolCallIconOrSpinner loading={showProcessing} type={toolCall?.type ?? null} />
        </div>
        <div className={classes.titleContainer}>
          <Typography
            className={clsx(classes.title, showProcessing && classes.processingText)}
            component="span"
          >
            {shownInfo.title}
          </Typography>
        </div>
      </div>
      <div className={classes.row}>
        <div className={classes.iconDiv}>
          <div className={classes.verticalLine} />
        </div>
        <div className={classes.textDiv}>
          <div className={clsx(classes.description, showProcessing && classes.processingText)}>
            <ToolCallDescription
              description={shownInfo.description}
              workflowLaunchDetails={workflowLaunchDetails}
            />
          </div>
          {toolCall?.type === 'askUser' && <AskUserInlineSummary output={toolOutput?.output} />}
          <QuestionRunProgressBar
            action={getQuestionRunCardAction({
              messageId: message.id,
              questionRunBatches: message.data.questionRunBatches,
              savableBatchId: getSavableBatchId(toolCall),
              stoppableBatchId,
            })}
            wait={questionRunBar}
          />
          <InlineWorkflowSearchResult result={inlineWorkflowSearchResult} />
        </div>
      </div>
    </div>
  );
});

/**
 * Stop while the turn waits on the run; once a library-wide run finished, save
 * it as a report. Null otherwise, so the progress bar leaves out its action row.
 */
function getQuestionRunCardAction({
  messageId,
  questionRunBatches,
  savableBatchId,
  stoppableBatchId,
}: {
  messageId: UUID;
  questionRunBatches: ChatAssistantMessage['data']['questionRunBatches'];
  savableBatchId: string | null;
  stoppableBatchId: string | null;
}) {
  if (stoppableBatchId)
    return <StopQuestionRunButton batchId={stoppableBatchId} messageId={messageId} />;
  if (savableBatchId)
    return (
      <SaveQuestionRunAsReportButton
        batchId={savableBatchId}
        messageId={messageId}
        savedReport={questionRunBatches?.[savableBatchId]?.savedReport ?? null}
      />
    );
  return null;
}

/**
 * Stops the run the turn is waiting on. The turn then resumes on its next
 * poll and answers from the documents that had finished.
 */
function StopQuestionRunButton({
  batchId,
  messageId,
}: {
  batchId: string | null;
  messageId: UUID;
}) {
  const { classes } = useToolCallStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [cancelQuestionRunBatch, { data, loading }] = Apollo.useCancelQuestionRunBatchMutation();
  // Held after a stop lands, until the card leaves its wait; a failed stop can be tried again.
  const isStopping = loading || Boolean(data);
  const handleStop = useCallback(async () => {
    try {
      if (!batchId) return;
      await cancelQuestionRunBatch({ variables: { batchId, messageId } });
    } catch {
      enqueueSnackbar('Could not stop the run. Please try again.', { variant: 'error' });
    }
  }, [batchId, cancelQuestionRunBatch, enqueueSnackbar, messageId]);

  return (
    <AlineButton
      buttonClassName={classes.stopRunButton}
      color="primary"
      disabled={isStopping}
      size="small"
      variant="text"
      onClick={handleStop}
    >
      <span aria-hidden className={classes.stopSquare} />
      {isStopping ? 'Stopping...' : 'Stop run'}
    </AlineButton>
  );
}

function InlineWorkflowSearchResult({ result }: { result: WorkflowAiToolCallResult | null }) {
  const { classes } = useToolCallStyles();
  if (!result) return null;
  if (result.status === 'errored') {
    return (
      <Typography className={classes.inlineWorkflowResultSummary} color="error">
        {result.errorMessage}
      </Typography>
    );
  }

  const candidates = getWorkflowResultCandidates(result);
  if (!candidates) return null;

  return (
    <div className={classes.inlineWorkflowResultWrapper}>
      <WorkflowResultCandidateList
        candidates={candidates}
        defaultExpanded={false}
        result={result}
        title={getWorkflowSearchResultTitle(candidates.length)}
      />
    </div>
  );
}

function ToolCallDescription({
  description,
  workflowLaunchDetails,
}: {
  description: React.ReactNode;
  workflowLaunchDetails: WorkflowLaunchDetails | null;
}) {
  if (workflowLaunchDetails) {
    return <WorkflowLaunchDescription details={workflowLaunchDetails} fallback={description} />;
  }

  return description;
}

interface WorkflowLaunchDetails {
  approved: boolean | undefined;
  result: WorkflowAiResult | null;
  templateName: string;
  templateWorkflowId: string;
  workflowName: string;
}

function WorkflowLaunchDescription({
  details,
  fallback,
}: {
  details: WorkflowLaunchDetails;
  fallback: React.ReactNode;
}) {
  const { approved, result, templateName, templateWorkflowId, workflowName } = details;
  if (result?.status === 'errored') return result.errorMessage;
  if (approved === false) return 'Workflow launch denied.';
  if (approved !== true && result?.status !== 'launched') return fallback;

  const launchedWorkflowLink =
    result?.status === 'launched'
      ? link.WorkflowDetailTabSummary({
          workflowId: result.workflow.workflowId,
        })
      : null;
  const templateUrl = link.TemplateSelectedTemplateSummary({
    workflowId: templateWorkflowId,
  });

  return (
    <>
      Launched &quot;
      {launchedWorkflowLink ? (
        <WorkflowEntityLink title={workflowName} url={launchedWorkflowLink} />
      ) : (
        workflowName
      )}
      &quot; from template &quot;
      <WorkflowEntityLink title={templateName} url={templateUrl} />
      &quot;
    </>
  );
}

function WorkflowEntityLink({ title, url }: { title: string; url: string }) {
  return <EntityLink title={title} url={url} />;
}

function TaskEntityLink({ title, url }: { title: string; url: string | null }) {
  if (!url) return title;
  return <EntityLink title={title} url={url} />;
}

function EntityLink({ title, url }: { title: string; url: string }) {
  return (
    <MuiLink
      href={url}
      onClick={event => {
        if (event.defaultPrevented) return;
        handleOpenInAlineTab(event, url, { title });
      }}
    >
      {title}
    </MuiLink>
  );
}

function WorkflowCreateDraftDescription({
  mentionEntityContext,
  toolCall,
}: {
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>;
  toolCall: Extract<ToolCallSafeParseResult, { type: 'workflowCreateDraft' }>;
}) {
  const createdWorkflowId =
    toolCall.result?.status === 'created_draft' ? toolCall.result.workflow.workflowId : null;
  const workflowLabel = createdWorkflowId
    ? getWorkflowLabel(createdWorkflowId, mentionEntityContext, toolCall.workflowName)
    : toolCall.workflowName;

  return (
    <>
      &quot;
      {createdWorkflowId ? (
        <WorkflowEntityLink
          title={workflowLabel}
          url={getWorkflowEntityUrl(createdWorkflowId, mentionEntityContext)}
        />
      ) : (
        workflowLabel
      )}
      &quot;.
    </>
  );
}

function WorkflowSearchDescription({ retrievalQueries }: { retrievalQueries: string[] }) {
  const normalizedRetrievalQueries = retrievalQueries
    .map(retrievalQuery => retrievalQuery.trim())
    .filter(Boolean);
  return <>Query: {normalizedRetrievalQueries.join('; ')}.</>;
}

function getSemanticSearchDisplayQueries(
  toolCall: Extract<ToolCallSafeParseResult, { type: 'semanticSearch' }>
) {
  if (toolCall.retrievalQueries?.length) return toolCall.retrievalQueries;
  if (toolCall.query) return [toolCall.query];
  return [];
}

/**
 * An analysis card that is waiting on an approved question run shows the run
 * and its progress instead of the empty analysis it would otherwise render.
 */
function getAnalysisQuestionRunWaitInfo({
  loading,
  pause,
  toolCall,
}: {
  loading: boolean;
  pause: DocumentExtractionPause | undefined;
  toolCall: ToolCallSafeParseResult | null;
}) {
  if (toolCall?.type !== 'reportAnalysis' && toolCall?.type !== 'documentBaseAnalysis') return null;
  const { questionRun } = toolCall;
  if (questionRun?.status !== 'waiting') return null;
  if (!loading) return { ...INTERRUPTED_QUESTION_RUN_COPY, type: toolCall.type };
  const progress = getQuestionRunProgress(pause, toolCall.id);
  return { ...getQuestionRunWaitCopy({ progress, questionRun }), type: toolCall.type };
}

/**
 * A system approval is the server's own pause, such as waiting on a question
 * run, so it stops counting once the turn has ended. A turn that errors while
 * paused never resolves it.
 */
function getIsAwaitingApproval({
  isTurnOver,
  toolCallId,
  uiParts,
}: {
  isTurnOver: boolean;
  toolCallId: string;
  uiParts: unknown[] | null | undefined;
}) {
  const pending = getPendingToolApprovalForToolCall(uiParts, toolCallId);
  if (!pending) return false;
  return !pending.part.approval?.system || !isTurnOver;
}

type RunCardWait = ReturnType<typeof getQuestionRunWaitForBatch>;

/** The wait a run card shows while the turn is still waiting on its batch. */
function getRunCardWait({
  isTurnOver,
  pause,
  toolCall,
}: {
  isTurnOver: boolean;
  pause: DocumentExtractionPause | undefined;
  toolCall: ToolCallSafeParseResult | null;
}): RunCardWait {
  if (toolCall?.type !== 'runExtractionQuestions' || isTurnOver) return null;
  return getQuestionRunWaitForBatch(pause, toolCall.batchId);
}

/** The finished run card's batch, when its run covered the library and can be saved as a report. */
function getSavableBatchId(toolCall: ToolCallSafeParseResult | null): string | null {
  if (toolCall?.type !== 'runExtractionQuestions' || toolCall.status !== 'finished') return null;
  if (!toolCall.savableAsReport) return null;
  return toolCall.batchId ?? null;
}

/**
 * The batch the card can stop: the run card's own while the turn waits on it,
 * or, for a batch recorded without a run card, the one the analysis waits on.
 */
function getStoppableBatchId({
  isAnalysisWaiting,
  pause,
  runCardWait,
  toolCall,
}: {
  isAnalysisWaiting: boolean;
  pause: DocumentExtractionPause | undefined;
  runCardWait: RunCardWait;
  toolCall: ToolCallSafeParseResult | null;
}): string | null {
  if (toolCall?.type === 'runExtractionQuestions')
    return runCardWait ? (toolCall.batchId ?? null) : null;
  if (!isAnalysisWaiting || !toolCall) return null;
  return getQuestionRunBatchId(pause, toolCall.id);
}

/**
 * The run's progress bar while the turn waits, and its final count once the
 * run is over. The run card carries it; the analysis card only for a batch
 * recorded without a run card.
 */
function getQuestionRunBarState({
  isProcessing,
  pause,
  runCardWait,
  toolCall,
}: {
  isProcessing: boolean;
  pause: DocumentExtractionPause | undefined;
  runCardWait: RunCardWait;
  toolCall: ToolCallSafeParseResult | null;
}): QuestionRunBarState | null {
  switch (toolCall?.type) {
    case 'reportAnalysis':
    case 'documentBaseAnalysis': {
      const { questionRun } = toolCall;
      if (questionRun?.status === 'finished') return getFinishedQuestionRunBar(questionRun);
      if (!isProcessing || questionRun?.status !== 'waiting') return null;
      return { progress: getQuestionRunProgress(pause, toolCall.id), total: questionRun.total };
    }
    case 'runExtractionQuestions': {
      if (runCardWait) return { progress: runCardWait.progress, total: toolCall.queuedCount };
      if (toolCall.status !== 'finished' || toolCall.done === undefined) return null;
      return {
        finished: true,
        progress: { done: toolCall.done, total: toolCall.queuedCount },
        total: toolCall.queuedCount,
      };
    }
    default:
      return null;
  }
}

/** A finished run's count. Older cards have none, so only a full run can be shown. */
function getFinishedQuestionRunBar(questionRun: AnalysisQuestionRun): QuestionRunBarState | null {
  const done =
    questionRun.done ?? (questionRun.outcome === 'ready' ? questionRun.total : undefined);
  if (done === undefined) return null;
  return { finished: true, progress: { done, total: questionRun.total }, total: questionRun.total };
}

function AnalysisDescriptionWithRun({
  children,
  questionRun,
}: {
  children: React.ReactNode;
  questionRun: AnalysisQuestionRun | undefined;
}) {
  const runLine = describeFinishedQuestionRun(questionRun);
  return (
    <>
      {Boolean(runLine) && <div>{runLine}</div>}
      {children}
    </>
  );
}

type RunExtractionQuestionsCard = Extract<
  ToolCallSafeParseResult,
  { type: 'runExtractionQuestions' }
>;

function getRunCardTitle(toolCall: RunExtractionQuestionsCard, loading: boolean) {
  if (toolCall.status === 'finished') return 'Ran extraction questions';
  if (loading) return 'Running extraction questions...';
  return 'Started extraction questions';
}

/**
 * "Running Governing Law on 42 documents. 3 locked answers were left as is."
 * Once finished: "Ran Governing Law on 42 documents.", and a note when some did not finish.
 */
function describeQuestionRun(toolCall: RunExtractionQuestionsCard) {
  const titles = toolCall.questions.map(question => question.title).join(', ');
  const documents = `${toolCall.queuedCount} ${toolCall.queuedCount === 1 ? 'document' : 'documents'}`;
  const verb = toolCall.status === 'finished' ? 'Ran' : 'Running';
  const run = titles ? `${verb} ${titles} on ${documents}.` : `${verb} on ${documents}.`;
  const unfinished =
    toolCall.status === 'finished' &&
    toolCall.done !== undefined &&
    toolCall.done < toolCall.queuedCount
      ? 'Some documents did not finish.'
      : null;
  const locked = toolCall.skippedLockedCount
    ? `${toolCall.skippedLockedCount} locked ${toolCall.skippedLockedCount === 1 ? 'answer was' : 'answers were'} left as is.`
    : null;
  return [run, unfinished, locked].filter(Boolean).join(' ');
}

function getQueryDocumentDisplayQueries(
  toolCall: Extract<ToolCallSafeParseResult, { type: 'queryDocument' }>
) {
  if (toolCall.retrievalQueries?.length) return toolCall.retrievalQueries;
  if (toolCall.question) return [toolCall.question];
  return [];
}

function getWorkflowResult(output: unknown) {
  const parsed = WorkflowAiResultSchema.safeParse(output);
  return parsed.success ? parsed.data : null;
}

function getWorkflowToolCallResult(output: unknown) {
  const parsed = WorkflowAiToolCallResultSchema.safeParse(output);
  return parsed.success ? parsed.data : null;
}

function getInlineWorkflowSearchResult({ toolCall }: { toolCall: ToolCallSafeParseResult | null }) {
  if (!toolCall) return null;
  if (toolCall.type !== 'workflowSearchExisting' && toolCall.type !== 'workflowSearchTemplates')
    return null;

  return getWorkflowToolCallResult(toolCall.result);
}

function getWorkflowSearchResultTitle(count: number) {
  return `Found ${count} search ${count === 1 ? 'result' : 'results'}`;
}

function ToolCallIconOrSpinner({ loading, type }: { loading: boolean; type: ToolCallType | null }) {
  if (loading) return <CircularProgress size={14} />;
  if (!type) return <ToolCallQuestionMarkIcon {...toolIconProps} />;

  return <ToolCallIcon type={type} />;
}

const useWebPageContentURLsStyles = makeStyles()(() => ({
  root: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
}));

function WebPageContentURLs({ loading, urls }: { loading: boolean; urls: string[] }): JSX.Element {
  const { classes } = useWebPageContentURLsStyles();
  const text = loading
    ? 'Retrieving web page contents for URL(s):'
    : 'Retrieved web page contents for URL(s):';

  return (
    <div className={classes.root}>
      {text}
      {urls.map((url, index) => (
        <LinkChip key={index} href={url} />
      ))}
    </div>
  );
}

const useSwapAgentTitleStyles = makeStyles()(() => ({
  root: {
    alignItems: 'center',
    display: 'flex',
    gap: 4,
  },
}));

function SwapAgentTitle({
  loading,
  toolCall,
}: {
  loading: boolean;
  toolCall: SwapAgentToolCall;
}): JSX.Element {
  const { classes } = useSwapAgentTitleStyles();
  const toAgent = getAgentInfo(toolCall.toAgent).displayName;
  const fromAgent = getAgentInfo(toolCall.fromAgent).displayName;
  const baseTitle = loading
    ? `Transferring from ${fromAgent} to ${toAgent}...`
    : `Transferred from ${fromAgent} to ${toAgent}`;
  const originalModelNameText = toolCall.originalModel
    ? `Model ${getAiModelDisplayName(toolCall.originalModel)} failed`
    : 'Model failed';

  if (!toolCall.fallbackModel) return <Typography component="span">{baseTitle}</Typography>;

  return (
    <span className={classes.root}>
      {baseTitle}
      <Spacer />
      <AlineTooltip
        arrow
        placement="top"
        title={`${originalModelNameText}, falling back to ${getAiModelDisplayName(toolCall.fallbackModel)}. Response quality may be different than normal.`}
      >
        <WarningIcon style={{ height: toolIconProps.height, width: toolIconProps.width }} />
      </AlineTooltip>
    </span>
  );
}

const useReportAnalysisStyles = makeStyles()(theme => ({
  coverageNote: {
    color: theme.palette.text.secondary,
    display: 'flex',
    flexDirection: 'column',
    fontSize: 10,
    fontStyle: 'italic',
    gap: theme.spacing(0.25),
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
}));

/**
 * Coverage shape as it arrives on the analysis tool-call cards. Derived from the
 * parsed tool-call union — not hand-copied — so a change to the zod
 * `analysisCoverageSchema` surfaces here as a type error instead of drifting
 * silently (extra fields would otherwise be tolerated). Both analysis variants
 * carry the same `coverage`; `documentBaseAnalysis` is picked arbitrarily.
 */
type AnalysisCoverage = NonNullable<
  Extract<ToolCallSafeParseResult, { type: 'documentBaseAnalysis' }>['coverage']
>;

/**
 * Plain-language completeness notes for the analysis card: which questions were
 * only partially answered across the scope, and whether the result was capped.
 * Mirrors the caveats the summarizer is told to make in prose.
 *
 * The capped note avoids naming the rows, because what got capped depends on the
 * query: a list is capped in documents, a grouped count or aggregate in groups.
 */
function coverageNotes(coverage: AnalysisCoverage): string[] {
  const notes = coverage.questions.flatMap(q => {
    const note = getQuestionCoverageNote(q, coverage);
    return note ? [note] : [];
  });
  if (coverage.truncated) {
    notes.push(
      `${coverage.matchedCount} documents matched; the result is capped and may be partial.`
    );
  }
  return notes;
}

/**
 * Counted among the documents the filters matched, so it agrees with the rows
 * shown. A card saved before that count existed falls back to the whole scope.
 */
function getQuestionCoverageNote(
  question: AnalysisCoverage['questions'][number],
  coverage: AnalysisCoverage
): string | null {
  const { matchedAnsweredCount, questionText } = question;
  if (matchedAnsweredCount === undefined) {
    if (question.answeredCount >= coverage.scopeTotal) return null;
    return `Only ${question.answeredCount} of ${coverage.scopeTotal} documents have an extracted "${questionText}".`;
  }
  if (matchedAnsweredCount >= coverage.matchedCount) return null;
  return `Only ${matchedAnsweredCount} of ${coverage.matchedCount} matching documents have an extracted "${questionText}".`;
}

function ReportAnalysisDescription({
  columnLabels,
  coverage,
  reports = [],
  rows,
  sql,
  toolCallId,
  totalCount,
  userQuestion,
}: {
  columnLabels: AnalysisColumnLabels;
  sql: string;
  toolCallId: string;
  userQuestion: string;
  coverage?: AnalysisCoverage;
  reports?: { id: string; name: string }[];
  rows?: AnalysisRow[];
  totalCount?: number;
}): JSX.Element {
  const { classes } = useReportAnalysisStyles();
  const message = useChatAssistantMessage();
  const notes = coverage ? coverageNotes(coverage) : [];
  const source = getAnalysisPanelSource({ messageId: message.id, rows, toolCallId, totalCount });

  return (
    <div className={classes.root}>
      <span>
        {reports.length > 0 ? (
          <>
            Analyzed{' '}
            {reports.map((report, index) => (
              <React.Fragment key={report.id}>
                {index > 0 && ', '}
                <EntityLink title={report.name} url={link.ReportDetail({ reportId: report.id })} />
              </React.Fragment>
            ))}
            : {userQuestion}
          </>
        ) : (
          `Analyzed your document library: ${userQuestion}`
        )}
      </span>
      {notes.length > 0 && (
        <div className={classes.coverageNote}>
          {notes.map((note, i) => (
            <span key={i}>{note}</span>
          ))}
        </div>
      )}
      <AnalysisQueryPanel columnLabels={columnLabels} source={source} sql={sql} />
    </div>
  );
}

/**
 * How an answered connect pause went, read from the tool output the card wrote.
 *
 * Anything unreadable returns null rather than guessing: reporting "connected"
 * for a pause the user actually declined would be worse than saying nothing.
 */
function getExternalConnectStatus(toolOutput: unknown): 'connected' | 'dismissed' | null {
  if (!getIsPlainRecord(toolOutput)) return null;
  if (toolOutput.status === 'connected') return 'connected';
  if (toolOutput.status === 'dismissed') return 'dismissed';
  return null;
}

type ToolCallSafeParseResult = NonNullable<ReturnType<typeof toolCallSchema.safeParse>['data']>;

/**
 * Every finished load has a card above the answer. Suppress its inline call while allowing
 * the synthetic card itself to render.
 */
export function getIsLoadShownByPinnedCard({
  loadedVersionIds,
  toolCall,
}: {
  loadedVersionIds: ReadonlySet<string>;
  toolCall: { id: string; versionId: string };
}) {
  if (getIsPinnedSkillCardToolCallId(toolCall.id)) return false;
  return loadedVersionIds.has(toolCall.versionId);
}

/**
 * A finished load shows its name only once the server confirms the user can read the version:
 * the name in the tool call may belong to a skill the user can no longer see. Until the
 * mention query answers, the row keeps waiting instead of saying unavailable.
 */
export function getLoadSkillToolInfo({
  loading,
  skillVersion,
  toolCall,
}: {
  loading: boolean;
  skillVersion: SkillVersionMentionEntity | null;
  toolCall: Extract<ToolCallSafeParseResult, { type: 'loadSkill' }>;
}) {
  if (!loading && !skillVersion)
    return {
      description: 'Checking that you can use this skill.',
      title: 'Loading skill...',
      waiting: true,
    };
  const version = skillVersion?.__typename === 'AgentSkillVersion' ? skillVersion : null;
  if (!loading && !version)
    return {
      description: 'It may have been deleted, or you no longer have access to it.',
      title: 'Skill unavailable',
      waiting: false,
    };

  const name = toolCall.name || version?.name || 'skill';
  return {
    description: (
      <>
        Skill:{' '}
        <AlineTooltip arrow placement="top" title={version?.description || name}>
          <span>
            <EntityLink
              title={name}
              url={link.AdminAgentSkillDetail({ skillId: toolCall.skillId })}
            />
          </span>
        </AlineTooltip>
      </>
    ),
    title: getLoadSkillTitle({ failed: Boolean(toolCall.failed), loading }),
    waiting: false,
  };
}

function getLoadSkillTitle({ failed, loading }: { failed: boolean; loading: boolean }) {
  if (loading) return 'Loading skill...';
  return failed ? 'Could not load skill' : 'Loaded skill';
}

/**
 * The original proposal shows its persisted outcome; a successful save links to the editor.
 */
function getSkillProposalToolInfo({
  approvalReason,
  approved,
  loading,
  output,
  toolCall,
}: {
  approved: boolean | undefined;
  loading: boolean;
  output: unknown;
  toolCall: Extract<ToolCallSafeParseResult, { type: 'createSkill' | 'updateSkill' }>;
  approvalReason?: string;
}) {
  const presentation = getSkillAuthoringPresentation({
    approvalReason,
    approved,
    loading,
    output,
    toolCall,
  });
  return {
    description: presentation.skill ? (
      <EntityLink
        title={presentation.skill.name}
        url={link.AdminAgentSkillDetail({ skillId: presentation.skill.id })}
      />
    ) : (
      presentation.description
    ),
    title: presentation.title,
    type: toolCall.type,
  };
}

function getToolInfo({
  approvalReason,
  approved,
  clientInput,
  loading,
  mentionEntityContext,
  toolCall,
  toolInput,
  toolOutput,
}: {
  readonly approvalReason: string | undefined;
  readonly approved: boolean | undefined;
  readonly clientInput: ClientInputToolLookup | null;
  readonly loading: boolean;
  readonly mentionEntityContext: React.ContextType<typeof MentionEntityContext>;
  readonly toolCall: ToolCallSafeParseResult | null;
  readonly toolInput: unknown;
  readonly toolOutput: unknown;
}): {
  description: React.ReactNode;
  title: React.ReactNode;
  type: ToolCallType | 'unknown-tool';
} {
  if (!toolCall) {
    return {
      description: 'An unknown tool call was detected.',
      title: loading ? 'Processing tool call...' : 'Processed tool call',
      type: 'unknown-tool',
    };
  }

  const interruptedClientInput = getInterruptedClientInputToolInfo(toolCall, clientInput);
  if (interruptedClientInput) return interruptedClientInput;

  switch (toolCall.type) {
    case 'askUser':
      if (toolCall.preparing)
        return {
          description: 'Working out what it needs from you.',
          title: 'Preparing questions...',
          type: toolCall.type,
        };
      return {
        description: loading ? 'Waiting for your answer.' : 'Answer received.',
        title: loading ? 'Asking for input...' : 'User input provided',
        type: toolCall.type,
      };
    case 'calculator':
      return {
        description: loading
          ? `Computing expression: ${toolCall.expression}.`
          : `Computed expression: ${toolCall.expression}.`,
        title: loading ? 'Calculating...' : 'Calculated',
        type: toolCall.type,
      };
    case 'commentOnTask':
      return {
        description: loading ? 'Adding a task comment.' : 'Added a task comment.',
        title: loading ? 'Adding comment...' : 'Added comment',
        type: toolCall.type,
      };
    case 'compactPriorOutput':
      return {
        description: `Prior output from ${toolCall.sourceAgent} exceeded the context budget (${toolCall.originalTokens} tokens). Target compacted size: ${toolCall.targetTokens} tokens.`,
        title: loading ? 'Compacting prior output...' : 'Compacted prior output',
        type: toolCall.type,
      };
    case 'completeTask':
      return {
        description: loading ? 'Marking the task complete.' : 'Marked the task complete.',
        title: loading ? 'Completing task...' : 'Completed task',
        type: toolCall.type,
      };
    case 'completeWorkflow': {
      const workflowLabel = getWorkflowLabel(toolCall.workflowId, mentionEntityContext, 'workflow');
      const workflowUrl = getWorkflowEntityUrl(toolCall.workflowId, mentionEntityContext);
      return {
        description: (
          <>
            {loading ? 'Marking' : 'Marked'} &quot;
            <WorkflowEntityLink title={workflowLabel} url={workflowUrl} />
            &quot; complete.
          </>
        ),
        title: loading ? 'Completing workflow...' : 'Completed workflow',
        type: toolCall.type,
      };
    }
    case 'queryDocument': {
      const queryDocumentLabel = getDocumentLabel(toolCall.documentId, mentionEntityContext);
      if (loading && toolCall.waitingForExtraction) {
        return {
          description: `Awaiting document processing for "${queryDocumentLabel}"`,
          title: 'Awaiting document processing',
          type: toolCall.type,
        };
      }
      return {
        description: (
          <WorkflowSearchDescription retrievalQueries={getQueryDocumentDisplayQueries(toolCall)} />
        ),
        title: loading
          ? `Searching document ${queryDocumentLabel}...`
          : `Searched document ${queryDocumentLabel}`,
        type: toolCall.type,
      };
    }
    case 'readDocument': {
      const readDocumentLabel = getDocumentLabel(toolCall.documentId, mentionEntityContext);
      if (loading && toolCall.waitingForExtraction) {
        return {
          description: `Awaiting document processing for "${readDocumentLabel}"`,
          title: 'Awaiting document processing',
          type: toolCall.type,
        };
      }
      return {
        description: loading
          ? `Reading document: ${readDocumentLabel}.`
          : `Read document: ${readDocumentLabel}.`,
        title: loading ? 'Reading document...' : 'Read document',
        type: toolCall.type,
      };
    }
    case 'getTask':
      return {
        description: loading ? 'Reading the task details.' : 'Read the task details.',
        title: loading ? 'Reading task...' : 'Read task',
        type: toolCall.type,
      };
    case 'getTaskAgentThread':
      return {
        description: loading ? 'Reading the task agent thread.' : 'Read the task agent thread.',
        title: loading ? 'Reading task thread...' : 'Read task thread',
        type: toolCall.type,
      };
    case 'listTasks':
      return {
        description: loading ? "Listing the workflow's tasks." : "Listed the workflow's tasks.",
        title: loading ? 'Listing tasks...' : 'Listed tasks',
        type: toolCall.type,
      };
    case 'readIntakeForm': {
      const workflowLabel = getWorkflowLabel(toolCall.workflowId, mentionEntityContext);
      const workflowUrl = getWorkflowEntityUrl(toolCall.workflowId, mentionEntityContext);
      return {
        description: (
          <>
            {loading ? 'Reading' : 'Read'} intake form for &quot;
            <WorkflowEntityLink title={workflowLabel} url={workflowUrl} />
            &quot;.
          </>
        ),
        title: loading ? 'Reading intake form...' : 'Read intake form',
        type: toolCall.type,
      };
    }
    case 'updateIntakeForm':
      return {
        description: loading ? 'Filling in the intake form.' : 'Filled in the intake form.',
        title: loading ? 'Filling intake form...' : 'Filled intake form',
        type: toolCall.type,
      };
    case 'reassignTask':
      return {
        description: loading
          ? "Updating the task's assignees or reviewers."
          : "Updated the task's assignees or reviewers.",
        title: loading ? 'Updating task assignments...' : 'Updated task assignments',
        type: toolCall.type,
      };
    case 'replyInChannelThread': {
      const workflowLabel = getWorkflowLabel(toolCall.workflowId, mentionEntityContext, 'workflow');
      // The reply is mirrored into the workflow's activity, under the conversation it answers.
      const activityUrl = getWorkflowRoute(toolCall.workflowId, { tab: 'comments' });
      return {
        description: (
          <>
            {loading ? 'Posting' : 'Posted'} a reply in a conversation linked to &quot;
            <WorkflowEntityLink title={workflowLabel} url={activityUrl} />
            &quot;.
          </>
        ),
        title: loading ? 'Replying in conversation...' : 'Replied in conversation',
        type: toolCall.type,
      };
    }
    case 'research':
      return {
        description: `Research plan: ${toolCall.instructions}`,
        title: loading ? 'Researching the Internet...' : 'Researched the Internet',
        type: toolCall.type,
      };
    case 'getWebPageContents':
      return {
        description: <WebPageContentURLs loading={loading} urls={toolCall.urls} />,
        title: loading
          ? `Reading web page contents for ${toolCall.urls.length} URL${toolCall.urls.length === 1 ? '' : 's'}...`
          : `Read web page contents for ${toolCall.urls.length} URL${toolCall.urls.length === 1 ? '' : 's'}`,
        type: toolCall.type,
      };
    case 'reportAnalysis': {
      const reportList = toolCall.reports.map(r => r.name).join(', ');
      return {
        description: loading ? (
          `Analyzing ${reportList}: ${toolCall.userQuestion}`
        ) : (
          <AnalysisDescriptionWithRun questionRun={toolCall.questionRun}>
            <ReportAnalysisDescription
              columnLabels={toolCall.columnLabels}
              coverage={toolCall.coverage}
              reports={toolCall.reports}
              rows={toolCall.rows}
              sql={toolCall.sql}
              toolCallId={toolCall.id}
              totalCount={toolCall.totalCount}
              userQuestion={toolCall.userQuestion}
            />
          </AnalysisDescriptionWithRun>
        ),
        title: loading ? 'Analyzing report data...' : 'Analyzed report data',
        type: toolCall.type,
      };
    }
    case 'documentBaseAnalysis':
      return {
        description: loading ? (
          `Analyzing your document library: ${toolCall.userQuestion}`
        ) : (
          <AnalysisDescriptionWithRun questionRun={toolCall.questionRun}>
            <ReportAnalysisDescription
              columnLabels={toolCall.columnLabels}
              coverage={toolCall.coverage}
              rows={toolCall.rows}
              sql={toolCall.sql}
              toolCallId={toolCall.id}
              totalCount={toolCall.totalCount}
              userQuestion={toolCall.userQuestion}
            />
          </AnalysisDescriptionWithRun>
        ),
        title: loading ? 'Analyzing your documents...' : 'Analyzed your documents',
        type: toolCall.type,
      };
    case 'runExtractionQuestions':
      return {
        description: describeQuestionRun(toolCall),
        title: getRunCardTitle(toolCall, loading),
        type: toolCall.type,
      };
    case 'search':
      return {
        description: loading
          ? `Searching the Internet for: ${toolCall.query}.`
          : `Searched the Internet for: ${toolCall.query}.`,
        title: loading ? 'Searching the Internet...' : 'Searched the Internet',
        type: toolCall.type,
      };
    case 'searchPlaybooks':
      return {
        description: loading
          ? `Searching playbooks for: ${toolCall.query}.`
          : `Searched playbooks for: ${toolCall.query}.`,
        title: loading ? 'Searching playbooks...' : 'Searched playbooks',
        type: toolCall.type,
      };
    case 'searchSkills':
      return {
        description: loading
          ? `Searching your skills for: ${toolCall.query}.`
          : `Searched your skills for: ${toolCall.query}.`,
        title: loading ? 'Searching skills...' : 'Searched skills',
        type: toolCall.type,
      };
    case 'readSkill':
      return {
        description: 'Read the skill to change.',
        title: loading ? 'Reading skill...' : 'Read skill',
        type: toolCall.type,
      };
    case 'createSkill':
    case 'updateSkill':
      return getSkillProposalToolInfo({
        approvalReason,
        approved,
        loading,
        output: toolOutput,
        toolCall,
      });
    case 'loadSkill':
      return {
        description: 'Loaded a skill for this task.',
        title: loading ? 'Loading skill...' : 'Loaded skill',
        type: toolCall.type,
      };
    case 'readPlaybook':
      return {
        description: `Playbook: ${getPlaybookLabel(toolCall.playbookId, mentionEntityContext)}.`,
        title: loading ? 'Reading playbook...' : 'Read playbook',
        type: toolCall.type,
      };
    case 'readSkillResource':
      // Never the path: a stored file's path is a UUID, which means nothing to the user.
      return {
        description: toolCall.name ? `Skill file: ${toolCall.name}.` : null,
        title: loading ? 'Reading skill file...' : 'Read skill file',
        type: toolCall.type,
      };
    case 'searchSkillResources':
      return {
        description: `Search: ${toolCall.query}.`,
        title: loading ? 'Searching skill files...' : 'Searched skill files',
        type: toolCall.type,
      };
    case 'createReport':
      return {
        ...getReportCreationPresentation({ running: loading, toolCall }),
        type: toolCall.type,
      };
    case 'generatePlaybook': {
      if (toolCall.preparing) {
        return {
          description: 'Working out the categories and rules for your review.',
          title: 'Preparing playbook plan...',
          type: toolCall.type,
        };
      }
      const documentIds = toolCall.documentIds ?? [];
      const documentLine = documentIds.length ? (
        <div>
          {documentIds.length === 1 ? 'Document' : 'Documents'}:{' '}
          {documentIds
            .map(documentId => getDocumentLabel(documentId, mentionEntityContext))
            .join(', ')}
        </div>
      ) : null;
      if (approved === false) {
        return {
          description: documentLine,
          title: 'Playbook plan skipped',
          type: toolCall.type,
        };
      }
      /**
       * Between the plan being put up and the user answering, the tool has not
       * run: saying a generation started would be wrong, and the card is the
       * thing they are being asked to decide on.
       */
      if (!toolCall.generationJobId) {
        return {
          description: documentLine,
          title: 'Waiting on plan approval',
          type: toolCall.type,
        };
      }
      return {
        description: documentLine,
        title: loading ? 'Generating playbook...' : 'Started playbook generation',
        type: toolCall.type,
      };
    }
    case 'runPlaybook': {
      const playbookLabel = getPlaybookLabel(toolCall.playbookId, mentionEntityContext);
      const documentLabel = getDocumentLabel(toolCall.documentId, mentionEntityContext);

      return {
        description: (
          <>
            <div>Playbook: {playbookLabel}</div>
            <div>Document: {documentLabel}</div>
          </>
        ),
        title: loading ? 'Running playbook...' : 'Started playbook run',
        type: toolCall.type,
      };
    }
    case 'semanticSearch':
      return {
        description: (
          <WorkflowSearchDescription retrievalQueries={getSemanticSearchDisplayQueries(toolCall)} />
        ),
        title: loading ? 'Searching repository...' : 'Searched repository',
        type: toolCall.type,
      };
    case 'selectDocuments':
      return {
        description: 'Selected repository documents for downstream agents.',
        title: loading ? 'Selecting documents...' : 'Selected documents',
        type: toolCall.type,
      };
    case 'swapAgent': {
      return {
        description: getAgentInfo(toolCall.toAgent).description,
        title: <SwapAgentTitle loading={loading} toolCall={toolCall} />,
        type: toolCall.type,
      };
    }
    case 'modelFallback': {
      const toModel = getAiModelDisplayName(toolCall.toModel);
      const fromModel = getAiModelDisplayName(toolCall.fromModel);

      return {
        description: `AI model ${fromModel} failed, falling back to ${toModel}.`,
        title: `Retrying request...`,
        type: toolCall.type,
      };
    }
    case 'updateTaskDescription':
      return {
        description: loading ? 'Updating the task description.' : 'Updated the task description.',
        title: loading ? 'Updating description...' : 'Updated description',
        type: toolCall.type,
      };
    case 'workflowCollectIntakeForm':
      if (getIsCollectIntakeFormSkipped(toolOutput)) {
        return {
          description: 'Skipped the workflow intake form.',
          title: 'Skipped workflow intake',
          type: toolCall.type,
        };
      }
      return {
        description: loading
          ? 'Waiting for the workflow intake form.'
          : 'Workflow intake form submitted.',
        title: loading ? 'Collecting workflow intake...' : 'Collected workflow intake',
        type: toolCall.type,
      };
    case 'workflowCollectLaunchInfo':
      if (getIsCollectIntakeFormSkipped(toolOutput)) {
        return {
          description: 'Skipped the workflow launch information.',
          title: 'Skipped workflow launch information',
          type: toolCall.type,
        };
      }
      return {
        description: loading
          ? 'Waiting for workflow launch information.'
          : 'Workflow launch information submitted.',
        title: loading ? 'Collecting launch information...' : 'Collected launch information',
        type: toolCall.type,
      };
    case 'workflowCreateDraft': {
      if (toolCall.preparing) {
        return {
          description: 'Building the workflow structure for your review.',
          title: 'Preparing workflow details...',
          type: toolCall.type,
        };
      }
      if (toolCall.result?.status === 'errored') {
        return {
          description: toolCall.result.errorMessage,
          title: 'Could not create draft workflow',
          type: toolCall.type,
        };
      }
      if (approved === false) {
        return {
          description: (
            <WorkflowCreateDraftDescription
              mentionEntityContext={mentionEntityContext}
              toolCall={toolCall}
            />
          ),
          title: 'Draft workflow creation denied',
          type: toolCall.type,
        };
      }
      if (loading || toolCall.result?.status !== 'created_draft') {
        return {
          description: (
            <WorkflowCreateDraftDescription
              mentionEntityContext={mentionEntityContext}
              toolCall={toolCall}
            />
          ),
          title: 'Creating draft workflow...',
          type: toolCall.type,
        };
      }
      return {
        description: (
          <WorkflowCreateDraftDescription
            mentionEntityContext={mentionEntityContext}
            toolCall={toolCall}
          />
        ),
        title: getIsTemplateWorkflow(toolCall.result.workflow.workflowId, mentionEntityContext)
          ? 'Created draft template'
          : 'Created draft workflow',
        type: toolCall.type,
      };
    }
    case 'workflowLaunchTemplate':
      return {
        description: 'Review and approve this workflow launch.',
        title: loading ? 'Preparing workflow launch...' : 'Launch workflow',
        type: toolCall.type,
      };
    /**
     * The step in the timeline, not the thing to act on: the live card sits
     * above the composer while the turn is suspended, and this is what the
     * conversation still reads like once it has been answered.
     */
    /**
     * Reads as a wait while the user is away at the provider, the same way
     * `askUser` does: the turn is suspended on a person either way, and the
     * only honest thing to show is that it is still waiting on them.
     */
    case 'externalConnect': {
      const connectLabel = getExternalToolkitLabel(toolCall.toolkit);
      if (loading) {
        return {
          description: 'Waiting for you to finish connecting.',
          title: `Connecting ${connectLabel}...`,
          type: toolCall.type,
        };
      }
      // An answered pause says which way it went; an unreadable one claims neither.
      const connectStatus = getExternalConnectStatus(toolOutput);
      if (connectStatus === 'connected') {
        return {
          description: `${connectLabel} account connected.`,
          title: `Connected ${connectLabel}`,
          type: toolCall.type,
        };
      }
      if (connectStatus === 'dismissed') {
        return {
          description: `Continued without connecting ${connectLabel}.`,
          title: `Skipped connecting ${connectLabel}`,
          type: toolCall.type,
        };
      }
      return {
        description: getExternalConnectDescription(toolCall.toolkit),
        title: getExternalConnectTitle(toolCall.toolkit),
        type: toolCall.type,
      };
    }
    case 'externalTool':
      return {
        /**
         * Two different claims, so two lines. The criteria say what Aline asked
         * the vendor for; the outcome says what it could verify of the answer.
         * Collapsing them would leave a search that found nothing looking
         * identical to one that asked the wrong question.
         */
        description: (
          <>
            {toolCall.criteria ? <div>Search: {toolCall.criteria}</div> : null}
            <div>
              {toolCall.resolved
                ? getExternalToolResolutionLabel(toolCall.resolved)
                : `${getExternalToolkitLabel(toolCall.toolkit)}: ${toolCall.toolSlugs.join(', ')}.`}
            </div>
          </>
        ),
        title: getExternalToolCallTitle({
          toolSlugs: toolCall.toolSlugs,
          toolkit: toolCall.toolkit,
        }),
        type: toolCall.type,
      };
    case 'workflowSearchExisting':
      return {
        description: <WorkflowSearchDescription retrievalQueries={toolCall.retrievalQueries} />,
        title: loading ? 'Searching existing workflows...' : 'Searched existing workflows',
        type: toolCall.type,
      };
    case 'workflowSearchTemplates':
      return {
        description: <WorkflowSearchDescription retrievalQueries={toolCall.retrievalQueries} />,
        title: loading ? 'Searching workflow templates...' : 'Searched workflow templates',
        type: toolCall.type,
      };
    case 'readWorkflow': {
      const workflowId = toolCall.workflowId ?? getInputString(toolInput, 'workflowId');
      if (!workflowId) {
        return {
          description: loading
            ? 'Reading workflow structure and status.'
            : 'Read workflow structure and status.',
          title: loading ? 'Reading workflow...' : 'Read workflow',
          type: toolCall.type,
        };
      }
      const workflowLabel = getWorkflowLabel(workflowId, mentionEntityContext, 'workflow');
      const workflowUrl = getWorkflowEntityUrl(workflowId, mentionEntityContext);
      return {
        description: (
          <>
            {loading ? 'Reading' : 'Read'} structure and status for &quot;
            <WorkflowEntityLink title={workflowLabel} url={workflowUrl} />
            &quot;.
          </>
        ),
        title: loading ? 'Reading workflow...' : 'Read workflow',
        type: toolCall.type,
      };
    }
    case 'managePhases':
      return {
        description: loading
          ? `Review and approve this phase change (${toolCall.operation}).`
          : `Applied a phase change (${toolCall.operation}).`,
        title: loading ? 'Preparing phase change...' : 'Manage phases',
        type: toolCall.type,
      };
    case 'assignTask': {
      const taskId = toolCall.taskId ?? getInputString(toolInput, 'taskId');
      const taskLink = taskId
        ? getTaskEntityLink(taskId, mentionEntityContext)
        : { title: 'this task', url: null };
      if (approved === false) {
        return {
          description: (
            <>
              Assignment denied for &quot;
              <TaskEntityLink title={taskLink.title} url={taskLink.url} />
              &quot;.
            </>
          ),
          title: 'Task assignment denied',
          type: toolCall.type,
        };
      }
      if (loading) {
        return {
          description: (
            <>
              Assign &quot;
              <TaskEntityLink title={taskLink.title} url={taskLink.url} />
              &quot;.
            </>
          ),
          title: 'Preparing task assignment...',
          type: toolCall.type,
        };
      }
      return {
        description: (
          <>
            Assigned &quot;
            <TaskEntityLink title={taskLink.title} url={taskLink.url} />
            &quot;.
          </>
        ),
        title: 'Assigned task',
        type: toolCall.type,
      };
    }
  }
}

function getInputString(input: unknown, key: string) {
  if (!getIsPlainRecord(input)) return null;
  const value = input[key];
  return typeof value === 'string' ? value : null;
}

type ToolOutputLookup = NonNullable<ReturnType<typeof getToolOutputForToolCall>>;

type ClientInputToolLookup = {
  readonly output: unknown;
  readonly state: ToolOutputLookup['state'];
  readonly abandonedReason?: string;
  readonly errorText?: string;
};

/**
 * How a client-input pause ended without an answer. Canceled means the user
 * walked away from the turn (a new message superseded it); failed means the
 * tool itself errored before anyone could answer.
 */
type ClientInputToolInterruption =
  | { readonly kind: 'canceled'; readonly reason: string }
  | { readonly kind: 'failed'; readonly reason: string | undefined };

type ClientInputToolCall = Extract<
  ToolCallSafeParseResult,
  {
    type: 'askUser' | 'externalConnect' | 'workflowCollectIntakeForm' | 'workflowCollectLaunchInfo';
  }
>;

export function getClientInputToolLookup(
  toolOutput: ToolOutputLookup | null
): ClientInputToolLookup | null {
  if (!toolOutput) return null;

  return {
    abandonedReason: toolOutput.part.abandonedReason,
    errorText: toolOutput.part.errorText,
    output: toolOutput.output,
    state: toolOutput.state,
  };
}

export function getIsClientInputToolAwaitingUser(
  type: ToolCallType,
  toolOutput: ClientInputToolLookup | null
) {
  if (!getIsClientInputToolType(type)) return false;
  /**
   * Undefined output is also an unanswered question. A terminal error or an
   * abandonment marker is what separates a cancelled pause from one still open.
   */
  if (getClientInputToolInterruption(toolOutput)) return false;
  const output = toolOutput?.output;
  if (output === undefined || output === null) return true;
  if (getIsPlainRecord(output) && output.status === 'awaiting_user_input') return true;
  if (type === 'askUser') return getAskUserAnswers(output) === null;
  /**
   * A connect pause is answered with connected or dismissed. Until one of those
   * arrives the user is still away at the provider, which is the same kind of
   * wait `askUser` is in, and the only thing that makes the step render as one.
   */
  if (type === 'externalConnect') return getExternalConnectStatus(output) === null;
  return !getHasCollectedLaunchValues(output) && !getIsCollectIntakeFormSkipped(output);
}

/**
 * Labels for a client-input pause that closed without an answer. Without this
 * an abandoned question falls through to the "answered" copy, since nothing
 * in its output says otherwise. Null when the pause is still open, answered,
 * or the tool does not pause on the user at all.
 */
export function getInterruptedClientInputToolInfo(
  toolCall: ToolCallSafeParseResult,
  toolOutput: ClientInputToolLookup | null
): { description: string; title: string; type: ToolCallType } | null {
  if (!getIsClientInputToolCall(toolCall)) return null;
  const interruption = getClientInputToolInterruption(toolOutput);
  if (!interruption) return null;

  const canceled = interruption.kind === 'canceled';
  switch (toolCall.type) {
    case 'askUser':
      return {
        description: interruption.reason ?? 'The question failed before you could answer.',
        title: canceled ? 'Question canceled' : 'Could not ask for input',
        type: toolCall.type,
      };
    case 'externalConnect': {
      const connectLabel = getExternalToolkitLabel(toolCall.toolkit);
      return {
        description: interruption.reason ?? `Connecting ${connectLabel} failed.`,
        title: canceled
          ? `Canceled connecting ${connectLabel}`
          : `Could not connect ${connectLabel}`,
        type: toolCall.type,
      };
    }
    case 'workflowCollectIntakeForm':
      return {
        description:
          interruption.reason ?? 'The workflow intake form failed before it was submitted.',
        title: canceled ? 'Canceled workflow intake' : 'Could not collect workflow intake',
        type: toolCall.type,
      };
    case 'workflowCollectLaunchInfo':
      return {
        description:
          interruption.reason ?? 'The workflow launch information failed before it was submitted.',
        title: canceled ? 'Canceled launch information' : 'Could not collect launch information',
        type: toolCall.type,
      };
  }
}

function getHasCollectedLaunchValues(output: unknown) {
  if (!getIsPlainRecord(output)) return false;
  return (
    getIsPlainRecord(output.intakeFormValues) ||
    getIsPlainRecord(output.alineFieldValues) ||
    Array.isArray(output.instanceAssignments)
  );
}

function getIsCollectIntakeFormSkipped(output: unknown) {
  return getIsPlainRecord(output) && output.skipped === true;
}

function getPlaybookLabel(
  playbookId: string,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>
) {
  const playbook = mentionEntityContext.playbooks.get(playbookId);
  if (playbook?.__typename === 'Playbook') return playbook.title;
  if (playbook?.__typename === 'MentionAccessError')
    return getAccessErrorLabel('playbook', playbook.reason);
  return 'Unavailable playbook';
}

function getWorkflowLabel(
  workflowId: string,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>,
  fallback = 'Selected template'
) {
  const workflow = mentionEntityContext.workflows.get(workflowId);
  if (workflow?.__typename === 'Workflow') return workflow.name || 'Untitled Template';
  return fallback;
}

// A template opens on the template route; under /workflows it renders as a live workflow.
function getWorkflowEntityUrl(
  workflowId: string,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>
) {
  const isTemplate = getIsTemplateWorkflow(workflowId, mentionEntityContext);
  return getWorkflowRoute(workflowId, { isTemplate });
}

function getIsTemplateWorkflow(
  workflowId: string,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>
) {
  const workflow = mentionEntityContext.workflows.get(workflowId);
  return (
    workflow?.__typename === 'Workflow' && workflow.variant === Apollo.WorkflowVariant.Template
  );
}

function getTaskEntityLink(
  taskId: string,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>
) {
  const task = mentionEntityContext.tasks.get(taskId);
  switch (task?.__typename) {
    case 'Task':
      return {
        title: task.title || 'Untitled Task',
        url: link.WorkflowDetailTabTask({ taskId, workflowId: task.workflow.id }),
      };
    case 'MentionAccessError':
      return {
        title: getAccessErrorLabel('task', task.reason),
        url: null,
      };
    default:
      return { title: 'Task', url: null };
  }
}

function getDocumentLabel(
  documentId: string,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>
) {
  const document = mentionEntityContext.documents.get(documentId);
  if (document?.__typename === 'Document') return document.name;
  if (document?.__typename === 'MentionAccessError')
    return getAccessErrorLabel('document', document.reason);
  return 'Unavailable document';
}

function getAccessErrorLabel(
  entityLabel: 'document' | 'playbook' | 'task',
  reason: Apollo.MentionAccessErrorReason
) {
  if (reason === Apollo.MentionAccessErrorReason.Deleted) return `Deleted ${entityLabel}`;
  if (reason === Apollo.MentionAccessErrorReason.NoAccess) return `No access to ${entityLabel}`;

  return `Unavailable ${entityLabel}`;
}

/**
 * Convert modelName to user-friendly provider name
 */
export function getAiModelDisplayName(modelName: AiModel): string {
  switch (modelName) {
    case 'claude4Sonnet':
      return 'Claude 4 Sonnet';
    case 'claude37Sonnet':
      return 'Claude 3.7 Sonnet';
    case 'claude55Haiku':
      return 'Claude Haiku 5.5';
    case 'claude45Haiku':
      return 'Claude 4.5 Haiku';
    case 'claude45Sonnet':
      return 'Claude 4.5 Sonnet';
    case 'claude46Sonnet':
      return 'Claude 4.6 Sonnet';
    case 'claude55Sonnet':
      return 'Claude Sonnet 5.5';
    case 'claude5Sonnet':
      return 'Claude Sonnet 5';
    case 'claude5Fable':
      return 'Claude Fable 5';
    case 'claude55Opus':
      return 'Claude Opus 5.5';
    case 'claude5Opus':
      return 'Claude Opus 5';
    case 'claude46Opus':
      return 'Claude 4.6 Opus';
    case 'claude47Opus':
      return 'Claude 4.7 Opus';
    case 'claude48Opus':
      return 'Claude 4.8 Opus';
    case 'gemini25Flash':
      return 'Gemini 2.5 Flash';
    case 'gemini25FlashLite':
      return 'Gemini 2.5 Flash Lite';
    case 'gemini25Pro':
      return 'Gemini 2.5 Pro';
    case 'gemini3Pro':
      return 'Gemini 3 Pro';
    case 'gemini35Flash':
      return 'Gemini 3.5 Flash';
    case 'gemini35FlashLite':
      return 'Gemini 3.5 Flash Lite';
    case 'gemini36Flash':
      return 'Gemini 3.6 Flash';
    case 'gemini38Flash':
      return 'Gemini 3.8 Flash';
    case 'gemini37Flash':
      return 'Gemini 3.7 Flash';
    case 'gemini31FlashLite':
      return 'Gemini 3.1 Flash Lite';
    case 'openai4o':
      return 'OpenAI GPT-4o';
    case 'openai4omini':
      return 'OpenAI GPT-4o Mini';
    case 'openai41':
      return 'OpenAI GPT-4.1';
    case 'openai41Mini':
      return 'OpenAI GPT-4.1 Mini';
    case 'openaio1':
      return 'OpenAI o1';
    case 'openaio3':
      return 'OpenAI o3';
    case 'openaio3Mini':
      return 'OpenAI o3 Mini';
    case 'openai5':
      return 'OpenAI GPT-5';
    case 'openai5Mini':
      return 'OpenAI GPT-5 Mini';
    case 'openai51':
      return 'OpenAI GPT-5.1';
    case 'openai52':
      return 'OpenAI GPT-5.2';
    case 'openai54':
      return 'OpenAI GPT-5.4';
    case 'openai54Mini':
      return 'OpenAI GPT-5.4 Mini';
    case 'openai54Nano':
      return 'OpenAI GPT-5.4 Nano';
    case 'openai55':
      return 'OpenAI GPT-5.5';
    case 'openai61Sol':
      return 'OpenAI GPT-6.1 Sol';
    case 'openai6Sol':
      return 'OpenAI GPT-6 Sol';
    case 'openai6Luna':
      return 'OpenAI GPT-6 Luna';
    case 'openai6Astra':
      return 'OpenAI GPT-6 Astra';
    case 'openai56Sol':
      return 'OpenAI GPT-5.6 Sol';
    case 'openai56Terra':
      return 'OpenAI GPT-5.6 Terra';
    case 'openai56Luna':
      return 'OpenAI GPT-5.6 Luna';
    default:
      assertNeverSafely(modelName);
      return 'Unknown Model';
  }
}

type AgentInfo = { description: string; displayName: string };

/**
 * Maps agent enum values to user-facing labels. V1 agents (orchestrator,
 * documentAnalysis, etc.) are kept here so that historical AgentMessage
 * rows whose tool-call payloads carry these names still render — V1 itself
 * is gone but the rows persist.
 */
function getAgentInfo(agent: AgentName): AgentInfo {
  switch (agent) {
    case 'documentAnalysis':
      return {
        description:
          'The Document Analysis Agent analyzes documents to extract information and generate summaries.',
        displayName: 'Document Analysis Agent',
      };
    case 'draftDocument':
      return {
        description:
          'The Draft Document Agent drafts a document based on the provided instructions.',
        displayName: 'Draft Document Agent',
      };
    case 'draftDocumentLongContext':
      return {
        description:
          'The Draft Document (Long Context) Agent is used to draft a document based on the provided instructions and can handle longer context than the Draft Document Agent.',
        displayName: 'Draft Document (Long Context) Agent',
      };
    case 'editSelection':
      return {
        description:
          'The Edit Selection Agent suggests edits to a selection in the document based on the provided instructions.',
        displayName: 'Edit Selection Agent',
      };
    case 'editSelectionLongContext':
      return {
        description:
          'The Edit Selection (Long Context) Agent is used to suggest edits to a selection in the document based on the provided instructions and can handle longer context than the Edit Selection Agent.',
        displayName: 'Edit Selection (Long Context) Agent',
      };
    case 'generalLegal':
      return {
        description:
          'The General Legal Agent is a general legal agent that can help with a wide range of legal questions.',
        displayName: 'General Legal Agent',
      };
    case 'orchestrator':
      return {
        description:
          'The Orchestrator Agent orchestrates the conversation and ensures all agents work together.',
        displayName: 'Orchestrator Agent',
      };
    case 'redline':
      return {
        description:
          'The Redline Agent reviews documents and produces redlined suggestions for improvement.',
        displayName: 'Redline Agent',
      };
    case 'redlineLongContext':
      return {
        description:
          'The Redline (Long Context) Agent reviews documents and produces redlined suggestions for improvement and can handle longer context than the Redline Agent.',
        displayName: 'Redline (Long Context) Agent',
      };
    case 'suggestEdits':
      return {
        description:
          'The Suggest Edits Agent suggests edits to the document based on the provided instructions.',
        displayName: 'Suggest Edits Agent',
      };
    case 'suggestEditsLongContext':
      return {
        description:
          'The Suggest Edits (Long Context) Agent is used to suggest edits to the document based on the provided instructions and can handle longer context than the Suggest Edits Agent.',
        displayName: 'Suggest Edits (Long Context) Agent',
      };
    case 'workflow':
      return {
        description:
          'The Workflow Agent searches for existing workflows or templates, launches workflows from templates, and creates draft workflows.',
        displayName: 'Workflow Agent',
      };
    default:
      assertNeverSafely(agent);
      return { description: 'Unknown Agent', displayName: 'Unknown Agent' };
  }
}

function getIsClientInputToolType(type: ToolCallType): type is ClientInputToolCall['type'] {
  return (
    type === 'askUser' ||
    type === 'externalConnect' ||
    type === 'workflowCollectIntakeForm' ||
    type === 'workflowCollectLaunchInfo'
  );
}

function getIsClientInputToolCall(
  toolCall: ToolCallSafeParseResult
): toolCall is ClientInputToolCall {
  return getIsClientInputToolType(toolCall.type);
}

function getClientInputToolInterruption(
  toolOutput: ClientInputToolLookup | null
): ClientInputToolInterruption | null {
  if (!toolOutput) return null;
  if (toolOutput.abandonedReason) return { kind: 'canceled', reason: toolOutput.abandonedReason };
  if (toolOutput.state === 'output-error') return { kind: 'failed', reason: toolOutput.errorText };
  return null;
}
